import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import SiteHeader from './components/SiteHeader';
import { SettingsProvider } from './settings/SettingsContext';
import HomePage from './pages/HomePage';
import AddLanguagePage from './pages/AddLanguagePage';
import ReviewPage from './pages/ReviewPage';
import SessionBuilderPage from './pages/SessionBuilderPage';
import LanguagePage from './pages/LanguagePage';
import BookPage from './pages/BookPage';
import ChapterPage from './pages/ChapterPage';
import SectionPage from './pages/SectionPage';
import FlashcardsPage from './pages/FlashcardsPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import config from './config';

/**
 * Everything except the two auth pages sits behind ProtectedRoute.
 *
 * Unlike a catalog you can browse before signing up, the course content *is*
 * the product here, so there is no signed-out view of it to design. The API
 * enforces the same rule, so a deep link pasted into a signed-out browser
 * bounces to /login and returns to where it was pointing afterwards.
 */
function AppLayout() {
  return (
    <ProtectedRoute>
      <SettingsProvider>
        <SiteHeader />
        <Outlet />
      </SettingsProvider>
    </ProtectedRoute>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // When sign-ups are closed the route is not registered at all, so /register
  // falls through to the catch-all and lands on the sign-in page. RegisterPage
  // still handles the closed case itself, for a build where the two disagree.
  ...(config.registrationOpen
    ? [{ path: '/register', element: <RegisterPage /> }]
    : []),
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      // Deliberately not /languages/add: that would sit under the same prefix
      // as :languageCode and rely on route ranking to win, which is the kind of
      // thing that breaks quietly when a language code called "add" appears.
      { path: '/add-language', element: <AddLanguagePage /> },
      { path: '/review/:languageCode', element: <ReviewPage /> },
      { path: '/build/:languageCode', element: <SessionBuilderPage /> },
      { path: '/languages/:languageCode', element: <LanguagePage /> },
      { path: '/books/:bookId', element: <BookPage /> },
      { path: '/chapters/:chapterId', element: <ChapterPage /> },
      { path: '/sections/:sectionId', element: <SectionPage /> },
      { path: '/sections/:sectionId/flashcards', element: <FlashcardsPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '*', element: <HomePage /> },
    ],
  },
]);

export default function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </AuthProvider>
  );
}
